/**
 * DMS Document Picker — popover that searches DMS and calls onSelect.
 * Does not write to the server itself; parent decides what to do with the selection.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDocumentsList } from '@/features/documents/hooks/useDocuments';
import { useDebounce } from '@/hooks/useDebounce';
import type { Document } from '@/features/documents/types';

interface DmsDocumentPickerProps {
  /** The trigger element. Receives no props; wrap it in any button/icon you want. */
  trigger: ReactNode;
  /** Ids of documents already attached — filtered out of results. */
  excludeIds?: Set<string>;
  /** Width of the popover content panel. Defaults to "md". */
  size?: 'sm' | 'md';
  /** Called when the user picks a document. */
  onSelect: (doc: Document) => void;
  /** Formatter for the secondary line under each document. Defaults to original filename. */
  formatSecondary?: (doc: Document) => string | null;
}

const WIDTHS = { sm: 'w-72', md: 'w-80' } as const;
const SIZE_CLASSES = {
  sm: { input: 'h-7 text-xs pl-7', icon: 'h-3.5 w-3.5', iconLeft: 'left-2 top-2', item: 'px-3 py-1.5', primary: 'text-xs font-medium', secondary: 'text-[10px]' },
  md: { input: 'h-8 text-sm pl-8', icon: 'h-4 w-4', iconLeft: 'left-2 top-2', item: 'px-3 py-2', primary: 'text-sm font-medium', secondary: 'text-xs' },
} as const;

export function DmsDocumentPicker({
  trigger,
  excludeIds,
  size = 'md',
  onSelect,
  formatSecondary = (doc) => doc.currentVersion?.originalName ?? null,
}: DmsDocumentPickerProps) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);

  const { data: searchResults, isLoading: isSearching } = useDocumentsList(null, debouncedSearch);
  const documents = searchResults?.data ?? [];
  const filteredDocs = useMemo(
    () => (excludeIds ? documents.filter((d) => !excludeIds.has(d.id)) : documents),
    [documents, excludeIds],
  );

  const s = SIZE_CLASSES[size];

  const handleSelect = (doc: Document) => {
    onSelect(doc);
    setOpen(false);
    setSearchInput('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className={`${WIDTHS[size]} p-0`} align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className={`absolute ${s.iconLeft} ${s.icon} text-muted-foreground`} />
            <Input
              className={s.input}
              placeholder="Search DMS documents..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoFocus
            />
          </div>
        </div>
        <div className={size === 'sm' ? 'max-h-48 overflow-y-auto' : 'max-h-64 overflow-y-auto'}>
          {!debouncedSearch && (
            <p className="text-xs text-muted-foreground text-center py-3">Type to search</p>
          )}
          {debouncedSearch && isSearching && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {debouncedSearch && !isSearching && filteredDocs.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-3">No documents found</p>
          )}
          {filteredDocs.map((doc) => {
            const secondary = formatSecondary(doc);
            return (
              <button
                key={doc.id}
                type="button"
                className={`w-full text-left ${s.item} hover:bg-muted/50 transition-colors border-b last:border-b-0`}
                onClick={() => handleSelect(doc)}
              >
                <p className={`${s.primary} truncate`}>{doc.name}</p>
                {secondary && (
                  <p className={`${s.secondary} text-muted-foreground truncate`}>{secondary}</p>
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
